import React from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import HeroSection from "@/components/HeroSection";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";
import {
  Shield,
  Users,
  Target,
  Globe,
  Award,
  TrendingUp,
  Eye,
} from "lucide-react";

const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Security First",
      description:
        "We prioritize the security and privacy of our clients above all else.",
    },
    {
      icon: Users,
      title: "Expert Team",
      description:
        "Our team consists of certified security professionals with decades of combined experience.",
    },
    {
      icon: Target,
      title: "Customer Focus",
      description:
        "We tailor our solutions to meet the unique needs of each organization.",
    },
    {
      icon: Globe,
      title: "Regional Expertise",
      description:
        "Deep understanding of Middle East market dynamics and regulatory requirements.",
    },
  ];

  const stats = [
    { number: "500+", label: "Clients Served" },
    { number: "15+", label: "Countries" },
    { number: "24/7", label: "SOC Operations" },
    { number: "99.9%", label: "Uptime SLA" },
  ];

  const timeline = [
    {
      year: "2025",
      title: "Founded",
      description:
        "Gulf Digital established as a cybersecurity VAD in the Middle East region",
    },
    {
      year: "2025",
      title: "Regional Expansion",
      description: "Expanded operations across 3 countries in Middle East",
    },
  ];

  return (
    <>
      <SEO
        title="About Gulf Digital - Leading Cybersecurity VAD in Middle East"
        description="Gulf Digital is a premier Value Added Distributor specializing in cybersecurity solutions for the Middle East. Serving 500+ clients with 24/7 SOC, PAM, CIAM, and compliance advisory."
        url="/about"
        ogTitle="About Gulf Digital | Cybersecurity Solutions in Saudi Arabia"
        ogDescription="Gulf Digital is a trusted Value Added Distributor delivering Cybersecurity solutions in Saudi Arabia, serving 500+ clients across the Middle East with 24/7 SOC, PAM, CIAM, and compliance advisory."
        twitterTitle="About Gulf Digital | Cybersecurity Services in Saudi Arabia"
        twitterDescription="Learn how Gulf Digital delivers Cybersecurity services in Saudi Arabia, backed by 500+ clients served and 24/7 SOC, PAM, and CIAM expertise."
      />
      <StructuredData type="Organization" />

      <HeroSection
        eyebrow="About Us"
        title="Protecting Organizations Across the Middle East Region"
        subtitle="Gulf Digital is a premier Value Added Distributor committed to delivering world-class cybersecurity solutions and services across the Middle East ."
        variant="minimal"
      />

      {/* Mission Section */}
      <section className="py-20 bg-white" data-testid="mission-section">
        <div className="container-custom">
          <div className="py-[1rem] px-[2rem] grid grid-cols-1 lg:grid-cols-2 items-center rounded-xl shadow border-2 border-warm-gray-200 hover:border-brand-maroon transition-all duration-300 shadow-soft">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="hidden lg:flex bg-warm-gray-100 rounded-xl h-96 items-center justify-center">
                <Shield className="w-32 h-32 text-brand-maroon" />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="py-[1rem] px-[1rem] lg:py-[2rem] lg:px-[4rem]"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-4 text-justify">
                Our mission is to empower organizations across the Middle East
                with proactive, intelligent, and affordable
                cybersecurity solutions that safeguard their operations and
                growth.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed text-justify">
                We aim to establish the regional benchmark in delivering
                next-generation technologies, managed security services, and
                expert advisory — creating a resilient digital ecosystem where
                businesses can thrive securely, confidently, and sustainably.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-white" data-testid="vision-section">
        <div className="container-custom">
          <div className="py-[1rem] px-[2rem] grid grid-cols-1 lg:grid-cols-2 items-center rounded-xl shadow border-2 border-warm-gray-200 hover:border-brand-maroon transition-all duration-300 shadow-soft">
            {/* Left: Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="py-[1rem] px-[1rem] lg:py-[2rem] lg:px-[4rem]"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Our Vision
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-4 text-justify">
                Our vision is to become the leading force in transforming the
                cybersecurity landscape across the Middle East by
                making enterprise-grade protection accessible, adaptive, and
                impactful for every business.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed text-justify">
                We strive to simplify cybersecurity by integrating
                next-generation technologies, managed security services, and
                expert advisory — ensuring that organizations of all sizes can
                operate with confidence, resilience, and a strong return on
                investment.
              </p>
            </motion.div>

            {/* Right: Icon or Illustration */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="hidden lg:flex bg-warm-gray-100 rounded-xl h-96 items-center justify-center">
                <Eye className="w-32 h-32 text-brand-maroon" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section
      <section className="py-20 bg-brand-maroon" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-white/80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Values Section */}
      <section className="py-20 bg-warm-gray-50" data-testid="values-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Our Values"
            title="What Drives Us"
            description="Our core values guide everything we do and shape how we serve our clients."
            titleColor="foreground"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-brand-maroon/10 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-brand-maroon" />
                </div>
                <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white" data-testid="timeline-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Our Journey"
            title="Fueling the Next Era of Cyber Protection"
            description="From our founding, we’re shaping the path to regional cybersecurity leadership."
            titleColor="foreground"
          />
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-8 mb-12 last:mb-0"
              >
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-brand-maroon text-white flex items-center justify-center font-bold text-lg">
                    {item.year}
                  </div>
                </div>
                <div className="flex-grow pt-2">
                  <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional Presence */}
      <section className="py-20 bg-warm-gray-50" data-testid="presence-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Regional Presence"
            title="Serving the Middle East Region"
            description="With offices and partners across the Middle East , we're always close to our clients."
            titleColor="foreground"
          />
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-soft">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">
                  Comprehensive Coverage
                </h3>
                <p className="text-muted-foreground mb-6">
                  Our regional presence enables us to provide localized support
                  while maintaining global standards of excellence. We
                  understand the unique challenges and requirements of
                  organizations operating in the Middle East region.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-2">
                    <Award className="w-5 h-5 text-brand-maroon" />
                    <span>Certified security professionals</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Globe className="w-5 h-5 text-brand-maroon" />
                    <span>Multi-lingual support teams</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-brand-maroon" />
                    <span>Local compliance expertise</span>
                  </li>
                </ul>
              </div>
              <div className="bg-warm-gray-100 rounded-xl h-80 flex items-center justify-center">
                <Globe className="w-40 h-40 text-brand-maroon opacity-20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default About;
